/**
 * NDS Audit Details — API response key-to-label mappings and transformer.
 *
 * The API returns { ndsReports, dcRs, dailyActualActivityDeleteAudits }.
 * Each section can be a single object or an array of objects.
 * This mapper converts camelCase keys to human-readable column headers
 * and transforms the response into the TableData[] format consumed by the UI.
 */

// ─── Column label maps ───────────────────────────────────────────────────────

export const ndsReportLabelMap: Record<string, string> = {
  // id: 'ID',
  // leaveRequestId: 'Leave Request ID',
  divisionName: 'Division Name',
  employeeCode: 'Employee Code',
  // staffPositionId: 'Staff Position ID',
  staffName: 'Staff Name',
  // designation: 'Designation',
  // designationType: 'Designation Type',
  // headquarter: 'Headquarter',
  // state: 'State',
  // dateOfJoining: 'Date Of Joining',
  lastDcrDate: 'Last DCR Date',
  ndsDate: 'NDS Date',
  ndsStatus: 'NDS Status',
  reason: 'Reason',
  adminRemarks: 'Admin Remarks',
  actionDate: 'Action Date',
  adminUserName: 'Admin User Name',
  // userId: 'User ID',
  startDate: 'Start Date',
  endDate: 'End Date',
  appliedOn: 'Applied On',
  // status: 'Status',
  // divisionId: 'Division ID',
  // year: 'Year',
  // countryId: 'Country ID',
  // stateId: 'State ID',
  // hqId: 'HQ ID',
  isActive: 'Is Active',
  // roleId: 'Role ID',
  // positionStartDate: 'Position Start Date',
  createdOn: 'Created On',
  modifiedOn: 'Modified On',
  dcrID: 'DCR ID',
  dcrStatus: 'DCR Status',
};

export const daaLabelMap: Record<string, string> = {
  entityId: 'Entity ID',
  dailyPlanActivityId: 'Daily Plan Activity ID',
  dcrId: 'DCR ID',
  commonDailyActualActivityId: 'Common Daily Actual Activity ID',
  deletedOn: 'Deleted On',
  importingDailyActualActivityId: 'Importing Daily Actual Activity ID',
  deletedBy: 'Deleted By',
};

export const dcrLabelMap: Record<string, string> = {
  id: 'ID',
  date: 'Date',
  statusId: 'Status ID',
  remark: 'Remark',
  reviewerId: 'Reviewer ID',
  createdOn: 'Created On',
  createdBy: 'Created By',
  modifiedOn: 'Modified On',
  modifiedBy: 'Modified By',
  staffPositionId: 'Staff Position ID',
  isLocked: 'Is Locked',
  deviceType: 'Device Type',
  isUnlocked: 'Is Unlocked',
  deviceId: 'Device ID',
  isTransferred: 'Is Transferred',
};

export const ndsReportPartyDetailsAuditLabelMap: Record<string, string> = {
  dcrId: 'DCR ID',
  dailyActualActivityId: 'Daily Actual Activity ID',
  dailyPlanActivityId: 'Daily Plan Activity ID',
  partyId: 'Party ID',
  partyName: 'Party Name',
  partyTypeName: 'Party Type',
  recordType: 'Record Type',
};

// ─── Types ───────────────────────────────────────────────────────────────────

export interface NDSAuditApiResponse {
  dcRs: Record<string, any> | Record<string, any>[];
  dailyActualActivityDeleteAudits:
    | Record<string, any>
    | Record<string, any>[];
  ndsReports: Record<string, any> | Record<string, any>[];
  ndsReportPartyDetailsAudits?: Record<string, any> | Record<string, any>[];
}

export interface TableData {
  tableName: string;
  columns: string[];
  rows: Record<string, any>[];
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

/**
 * Convert a single object (or array of objects) into rows
 * whose keys are the human-readable column labels.
 */
const mapRows = (
  data: Record<string, any> | Record<string, any>[] | null | undefined,
  labelMap: Record<string, string>,
): Record<string, any>[] => {
  if (!data) return [];

  const items = Array.isArray(data) ? data : [data];

  return items.map(item => {
    const mappedRow: Record<string, any> = {};
    Object.keys(labelMap).forEach(key => {
      const label = labelMap[key];
      const value = item[key];

      if (value === null || value === undefined) {
        mappedRow[label] = '-';
      } else if (typeof value === 'boolean') {
        mappedRow[label] = value ? 'Yes' : 'No';
      } else {
        mappedRow[label] = String(value);
      }
    });
    return mappedRow;
  });
};

/**
 * Get ordered column labels from a label map.
 */
const getColumns = (labelMap: Record<string, string>): string[] =>
  Object.values(labelMap);

// ─── Main transformer ────────────────────────────────────────────────────────

/**
 * Transform the raw API response into an array of TableData
 * ready to be rendered by the screen component.
 */
export const transformNDSAuditResponse = (
  response: NDSAuditApiResponse,
): TableData[] => {
  return [
    {
      tableName: 'NDS',
      columns: getColumns(ndsReportLabelMap),
      rows: mapRows(response.ndsReports, ndsReportLabelMap),
    },
    {
      tableName: 'Daily Actual Activity',
      columns: getColumns(daaLabelMap),
      rows: mapRows(
        response.dailyActualActivityDeleteAudits,
        daaLabelMap,
      ),
    },
    {
      tableName: 'DCR',
      columns: getColumns(dcrLabelMap),
      rows: mapRows(response.dcRs, dcrLabelMap),
    },
    {
      tableName: 'NDS Party Details',
      columns: getColumns(ndsReportPartyDetailsAuditLabelMap),
      rows: mapRows(response.ndsReportPartyDetailsAudits, ndsReportPartyDetailsAuditLabelMap),
    },
  ];
};
